import React, {createContext, useCallback, useContext, useEffect, useMemo, useRef, useState} from "react";
import {debounce, flatten, isNull, isUndefined, set, sumBy, uniq} from "lodash";
import {MarketplaceContext} from "../../marketplaceContext.jsx";
import {EventBus} from "../../../bus/EventBus.jsx";
import {useListenToSocket} from "../../../components/socket/useListenToSocket.jsx";
import {CartModularProductContext} from "../../PricingCalculator/CartModularProductContext.jsx";
import {
    DEFAULT_DELIVERY_ARRIVING_TIME_RANGE,
    DEFAULT_DELIVERY_PRICE,
    ERROR_LEVEL,
    getDefaultTypeToShow,
    PLACE_ERROR,
    sendAlertToCsmWithEmail,
    typeToPluralHebrewName,
    WARN_LEVEL
} from "../../MarketplaceUtils.jsx";
import {PriceTypes} from "../../../admin/upload/PriceSelectorHelper.jsx";
import {getLogger} from "../../../Logger.jsx";
import {
    getDeliveryArea,
    getDeliveryPriceForBooking,
    getMergedDeliveryOptions,
    getUserChangedFormValues,
    removeFloorText,
    validateBooking,
    validateVoucherBooking
} from "../utils.jsx";
import {BOOKING_ERROR_TO_PANEL, BOOKING_TO_PANEL_COLORS, FIRST_ORDER, MIN_USER_FIELD_TRIGGER} from "../consts.jsx";
import {isCompanySubAdmin} from "../../../center/EXCenterHelper.jsx";
import {isProductHasShippingProvider} from "../../../admin/upload/utils.js";
import {ActivityLocationTypes, PaymentType} from "../../../consts.js";
import {HomeDeliveryUploadMethod} from "../../OrderLocationInput/consts.jsx";
import {HttpClient} from "../../../http/HttpClient.jsx";
import {
    createBookingEvent,
    createBookingEventValues,
    createVoucherBookingEvent,
    getOrderTip,
    getPriceByAmount
} from "../../MarketplaceHelper.jsx";
import {CouponDiscountTypes} from "../../../admin/AdminCoupons/consts.js";
import {getValuesForCreateEvent} from "./utils.js";
import {GoogleAnalytics} from "../../../GoogleAnalytics.jsx";
import {ConfigProvider, message, Typography} from "antd";
import {getOrderPrice, getTotalBudget} from "../../../utils/calculationUtils.jsx";
import {TickerNotification} from "../../../ordersCalendar/OrderTicker/index.jsx";
import {BookingCompleteModal} from "../BookingCompleteModal.jsx";
import {useQuery, useRequest} from "../../../utils/hooks.jsx";
import moment from "moment";
import {AsiaJerusalemTZ} from "../../../event/DaysMapper.jsx";
import {MarketplaceBookingContext} from "../MarketplaceBookingContext.jsx";
import {NOOP} from "../../../utils/NOOP.jsx";
import {CompanyInvoiceManage} from "../../../company/CompanyAccountProfile/CompanyInvoiceManage/index.jsx";
import {EditSubAdminModal} from "../../../company/CompanyAccountProfile/EditSubAdminModal.jsx";
import {editSubAdminPhoneHideFormFields} from "./consts.js";

export const ProductBookingContext = createContext({});

export const ProductBookingContextProvider = ({
    children,
    history,
    me = {},
    company = {},
    services = [],
    shouldNotifyOnWindowExit = true,
    setPlanBudgetOrder = NOOP,
    planBudgetOrder,
    onSyncUserBudget = NOOP,
    allowEmptyPackageByPriceType = [],
    fetchMe = NOOP,
    fetchCompany = NOOP,
    onSaveCompanyFinanceContact = NOOP
}) => {
    const {
        product,
        providerDetails,
        serviceId,
        providerId,
        productId,
        userFormValues,
        participants,
        highlightOrderPanel,
        setHighlightOrderPanel,
        productBundle,
        isProductBundle
    } = useContext(MarketplaceContext);

    const [isSocketError, setIsSocketError] = useState(null);

    const onReceivedBookingOfferSocketMessage = useCallback(message => {
        if (message.status === "success") {
            setCreatedEvent(message?.eventId);
            setEventProposalId(message.proposalId);
            setBookingCompleteModalVisibility(true);
            setBookingLoading(false);
        } else if (message.status === "error") {
            EventBus.triggerError("server-error", {
                content: {
                    description: [
                        `Unfortunately we didn't manage to send your offer :(`,
                        `Error: ${message.errorMessage}`
                    ]
                }
            });

            setBookingLoading(false);
        }
    }, []);

    useListenToSocket({eventName: "BOOKING_OFFER", onUpdate: onReceivedBookingOfferSocketMessage});
    useListenToSocket({eventName: "connect_error", onUpdate: () => setIsSocketError(true)});
    useListenToSocket({eventName: "connect", onUpdate: () => setIsSocketError(false)});

    const {selectedSubProducts} = useContext(CartModularProductContext);
    const [formValues, setFormValues] = useState({});
    const [loadingDeliveryPrice, setLoadingDeliveryPrice] = useState(false);
    const [updateDetailsModals, setUpdateDetailsModals] = useState(null);
    const [bookingCompleteModalVisibility, setBookingCompleteModalVisibility] = useState(false);
    const [creditCard, setCreditCard] = useState(false);
    const [bookingLoading, setBookingLoading] = useState(false);
    const [createdEvent, setCreatedEvent] = useState();
    const [eventProposalId, setEventProposalId] = useState();
    const [paymentType, setPaymentType] = useState(null);
    const [couponCode, setCouponCode] = useState(null);
    const [coupon, setCoupon] = useState(null);
    const [activeKey, setActiveKey] = useState(null);
    const [currentDeliveryArea, setCurrentDeliveryArea] = useState(null);
    const [shouldUpdateSubAdminDetails, setShouldUpdateSubAdminDetails] = useState(false);
    const [isConfirmButtonLoading, setConfirmButtonLoading] = useState(false);

    const productRef = useRef(product);

    let query = useQuery();

    const savedOrderURI = useMemo(() => {
        if (!query) return null;

        const fromSavedOrder = query.get("fromSavedOrder");
        const fromEventId = query.get("fromEventId");

        if (!fromSavedOrder && !fromEventId) {
            return null;
        }

        return fromEventId
            ? `/api/savedOrder/byEventId/${encodeURIComponent(fromEventId)}`
            : `/api/savedOrder/${encodeURIComponent(fromSavedOrder)}`;
    }, [query]);

    const [savedOrder] = useRequest(savedOrderURI, "get", null, [query], !!savedOrderURI);

    const calculateModularProductPrice = useMemo(() => {
        return formValues?.modularProducts?.reduce((acc, formValue) => {
            return acc + formValue?.price * formValue?.amount;
        }, 0);
    }, [formValues.modularProducts]);

    useEffect(() => {
        if (product?.chosenPriceType === PriceTypes.ByModular) {
            const isSavedOrderHasSubProducts = savedOrder?.modularProducts?.length;
            const modularProducts = product?.pricingTable?.byModular?.subProducts?.reduce((acc, subProduct) => {
                const foundProviderSubProduct = providerDetails.subProducts?.find(({id}) => id === subProduct?.id);

                if (foundProviderSubProduct?.isActive && subProduct.isActive) {
                    const foundSavedOrderSubProduct = savedOrder?.modularProducts.find(({id}) => id === subProduct.id);

                    if (foundSavedOrderSubProduct || isSavedOrderHasSubProducts) {
                        return [
                            ...acc,
                            {
                                ...subProduct,
                                ...foundProviderSubProduct,
                                amount: foundSavedOrderSubProduct ? foundSavedOrderSubProduct.amount ?? 0 : 0
                            }
                        ];
                    }

                    if (selectedSubProducts?.length) {
                        const foundSelectedProduct = selectedSubProducts?.find(({id}) => id === subProduct?.id);
                        return [
                            ...acc,
                            {
                                ...subProduct,
                                ...foundProviderSubProduct,
                                amount: foundSelectedProduct ? foundSelectedProduct.amount ?? 0 : 0
                            }
                        ];
                    }

                    return [...acc, {...subProduct, ...foundProviderSubProduct, amount: subProduct.defaultAmount ?? 0}];
                }

                return acc;
            }, []);

            setFormValues(current => ({...current, modularProducts}));
        }
    }, [product, providerDetails, selectedSubProducts]);

    const log = useMemo(
        () =>
            getLogger(
                {
                    serviceId,
                    providerId,
                    productId,
                    providerName: providerDetails?.businessName,
                    productName: product?.productName
                },
                "ProductDetails"
            ),
        [serviceId, providerId, productId, product, providerDetails]
    );

    useEffect(() => {
        if (product?.chosenPriceType) {
            setFormValues(current => ({...current, priceType: typeToPluralHebrewName(product?.chosenPriceType)}));
        }
    }, [product?.chosenPriceType]);

    useEffect(() => {
        if (product) {
            productRef.current = product;
        }
    }, [product]);

    const onExitWindow = useCallback(async e => {
        if (getUserChangedFormValues(userFormValues.current).length >= MIN_USER_FIELD_TRIGGER) {
            await sendAlertToCMSs(
                me?.companyId,
                null,
                "המשתמש מילא פרטים ונטש את עמוד ההזמנה",
                productRef.current?.productName,
                null
            );
            userFormValues.current = null;

            const confirmationMessage = "האם אתה בטוח שברצונך לצאת מהעמוד?";
            (e || window.event).returnValue = confirmationMessage;
            return confirmationMessage;
        }
    }, []);

    useEffect(() => {
        if (!shouldNotifyOnWindowExit) {
            return;
        }

        window.addEventListener("beforeunload", onExitWindow);

        return () => {
            window.removeEventListener("beforeunload", onExitWindow);
        };
    }, [onExitWindow, shouldNotifyOnWindowExit]);

    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    const isUserCompanySubAdmin = useMemo(() => isCompanySubAdmin(me), [me]);

    useEffect(() => {
        async function calculateDeliveryPrice() {
            setLoadingDeliveryPrice(true);

            const isSelfPickup = formValues?.chosenActivityLocation === ActivityLocationTypes.selfPickup;

            if (isSelfPickup) {
                setFormValues(prev => (prev.deliveryPrice === 0 ? prev : {...prev, deliveryPrice: 0}));
                setLoadingDeliveryPrice(false);
                return;
            }

            const isValidShippingProvidersFields = !!(isProductHasShippingProvider(product) && product?.productId);
            const isExcelHomeDelivery =
                formValues.chosenActivityLocation === ActivityLocationTypes.homeDelivery &&
                formValues.homeDeliveryUploadMethod === HomeDeliveryUploadMethod.excel;

            const isSingleHomeDelivery =
                formValues.chosenActivityLocation === ActivityLocationTypes.homeDelivery &&
                formValues.homeDeliveryUploadMethod === HomeDeliveryUploadMethod.singleEmployee;

            const isSingleOrManuelLocationDelivery =
                formValues.chosenActivityLocation === ActivityLocationTypes.manualLocation || isSingleHomeDelivery;

            if (isValidShippingProvidersFields && isExcelHomeDelivery && formValues.employeesExcel?.rowCount) {
                const deliveryProviderRes = await HttpClient.safeGet(
                    `/api/deliveryProvider/byId/${product.shippingProviders?.[0]}`
                );

                setFormValues(formValues => ({
                    ...formValues,
                    sourcePlaceId: product.address?.[0]?.placeId,
                    sourceAddressName: product.address?.[0]?.name,
                    ...(deliveryProviderRes?.homeDeliveryPrice
                        ? {deliveryPrice: deliveryProviderRes?.homeDeliveryPrice * formValues.employeesExcel?.rowCount}
                        : {})
                }));
                setLoadingDeliveryPrice(false);
            } else if (isValidShippingProvidersFields && isSingleOrManuelLocationDelivery) {
                const destinationPlaceId = isSingleHomeDelivery
                    ? formValues.singleEmployeeDetails.placeId
                    : formValues.addressId;

                const deliveryPriceRes = await HttpClient.safePost(`/api/products/shippingPrice?userId=${me?.userId}`, [
                    {
                        productId: product.productId,
                        destinationPlaceId
                    }
                ]);

                if (!deliveryPriceRes.error) {
                    const [firstDeliveryPriceRes] = deliveryPriceRes;

                    setFormValues(formValues => ({
                        ...formValues,
                        sourcePlaceId: firstDeliveryPriceRes.sourcePlaceId,
                        sourceAddressName: firstDeliveryPriceRes.sourceAddressName,
                        deliveryPrice: firstDeliveryPriceRes.price,
                        originalDeliveryPrice: firstDeliveryPriceRes?.originalDeliveryPrice
                    }));
                }

                setLoadingDeliveryPrice(false);
            } else {
                const officeAddress = {
                    address: isUserCompanySubAdmin ? me?.address : company?.address,
                    addressId: isUserCompanySubAdmin ? me?.addressId : company?.addressId
                };

                const searchedAddress = formValues?.singleEmployeeDetails?.address ?? formValues?.chosenAddress;
                const searchedAddressId = formValues?.singleEmployeeDetails?.addressId ?? formValues?.addressId;

                const address = {
                    address: searchedAddress,
                    addressId:
                        officeAddress?.address?.trim() === removeFloorText(searchedAddress?.trim())
                            ? searchedAddressId
                            : searchedAddressId
                };

                const deliveryArea = await getDeliveryArea(address, product, providerDetails, log);

                if (product) {
                    product.deliveryPrice = product.deliveryPrice ?? providerDetails.deliveryPrice;
                }

                const deliveryPrice = getDeliveryPriceForBooking(
                    formValues?.chosenAddress,
                    providerDetails,
                    product,
                    formValues,
                    deliveryArea,
                    log
                );

                setLoadingDeliveryPrice(false);
                setCurrentDeliveryArea(deliveryArea);

                setFormValues(prev => ({
                    ...prev,
                    deliveryPrice: deliveryPrice?.price,
                    errors: {
                        ...(prev.errors ?? {}),
                        [PLACE_ERROR]: deliveryPrice?.message
                            ? {
                                  level: deliveryPrice.price ? WARN_LEVEL : ERROR_LEVEL,
                                  message: deliveryPrice.message
                              }
                            : null
                    }
                }));
            }
        }

        calculateDeliveryPrice();
    }, [
        formValues.employeesExcel,
        formValues.chosenAddress,
        formValues.chosenActivityLocation,
        company?.address,
        company?.addressId,
        formValues.singleEmployeeDetails,
        product?.deliveryPrice,
        providerDetails?.deliveryOptions,
        log,
        me?.address,
        me?.addressId,
        isUserCompanySubAdmin
    ]);

    useEffect(() => {
        if (participants) {
            setFormValues(currentFormValues => set(currentFormValues, "amount", participants));
        }
    }, [participants]);

    const upgradeOptionsPrice = useMemo(
        () =>
            sumBy(formValues.upgradeOptions?.options, option =>
                option.type === "byUnit" ? option.price * (option.amount ?? 0) : option.price
            ),
        [formValues.upgradeOptions?.options, formValues.amount]
    );

    useEffect(() => {
        let total;

        switch (product?.chosenPriceType) {
            case "byModular":
                total = (calculateModularProductPrice ?? 0) + upgradeOptionsPrice;
                break;
            case "byVoucher":
                const voucherReceiversAmount = formValues.singleEmployeeDetails
                    ? 1
                    : formValues.employeesExcel?.rowCount;
                total = formValues.voucherOption?.redeemableValue * voucherReceiversAmount;
                break;
            default:
                const currentPrice = getPriceByAmount(formValues.amount, product);
                total = currentPrice
                    ? (
                          parseFloat(currentPrice.toString().replace(",", "")) *
                              (getDefaultTypeToShow(product) === "byGroup" ? 1 : parseFloat(formValues?.amount)) +
                          upgradeOptionsPrice
                      ).toFixed(0) - 0
                    : null;
        }

        if (!isNull(total) && !isUndefined(total) && !isNaN(total)) {
            onFormValueChange("offeredPrice", total);
        }
    }, [
        product,
        formValues?.amount,
        upgradeOptionsPrice,
        formValues?.modularProducts,
        formValues?.voucherOption,
        formValues?.singleEmployeeDetails,
        formValues.employeesExcel
    ]);

    const onFormValueChange = useCallback(
        (field, val) => {
            setFormValues(prevValues => set({...prevValues}, field, val));
        },
        [setFormValues]
    );

    useEffect(() => {
        onFormValueChange("deliveryPrice", DEFAULT_DELIVERY_PRICE);
    }, []);

    const onInvoiceCommentChange = useCallback(
        val => {
            setFormValues(prevValues => ({...prevValues, invoiceComment: val}));
        },
        [setFormValues]
    );

    const onInvoicePONumberChange = useCallback(
        val => {
            setFormValues(prevValues => ({...prevValues, invoicePONumber: val}));
        },
        [setFormValues]
    );

    const onSeparateInvoiceChange = useCallback(val => {
        setFormValues(prevValues => ({...prevValues, separateInvoice: val}));
    }, []);

    const onBudgetCategoriesPriorityChange = useCallback(val => {
        setFormValues(prevValues => ({...prevValues, budgetCategoriesPriority: val}));
    }, []);

    const couponDiscountAmount = useMemo(() => {
        if (coupon?.discountType === CouponDiscountTypes.ByPercentage) {
            return (formValues?.offeredPrice * coupon?.discount) / 100;
        }

        return coupon?.discount;
    }, [coupon, formValues?.offeredPrice]);

    const bookingError = useMemo(
        () =>
            providerDetails?.isVoucherProvider
                ? validateVoucherBooking(
                      formValues,
                      company,
                      paymentType,
                      product,
                      creditCard,
                      me,
                      formValues?.offeredPrice,
                      formValues?.deliveryPrice,
                      couponDiscountAmount
                  )
                : validateBooking(
                      me,
                      paymentType,
                      formValues?.offeredPrice,
                      product,
                      formValues,
                      creditCard,
                      company,
                      formValues?.deliveryPrice,
                      couponDiscountAmount,
                      formValues.companyDiscount,
                      0,
                      allowEmptyPackageByPriceType
                  ),
        [
            me,
            paymentType,
            formValues?.offeredPrice,
            product,
            formValues,
            creditCard,
            company,
            formValues?.deliveryPrice,
            providerDetails,
            coupon
        ]
    );

    const deliveryOptions = useMemo(() => {
        return getMergedDeliveryOptions(providerDetails, product);
    }, [providerDetails, product]);

    const valuesForCreateEvent = useMemo(() => {
        return getValuesForCreateEvent(
            formValues,
            deliveryOptions,
            serviceId,
            paymentType,
            formValues?.offeredPrice,
            company,
            formValues?.deliveryPrice,
            coupon,
            couponDiscountAmount,
            creditCard,
            upgradeOptionsPrice,
            planBudgetOrder,
            providerDetails
        );
    }, [
        providerDetails,
        product?.durationMinutes,
        providerDetails,
        serviceId,
        paymentType,
        formValues?.offeredPrice,
        company?.address,
        me.companyId,
        formValues,
        coupon,
        formValues?.deliveryPrice,
        creditCard,
        upgradeOptionsPrice,
        company?.branches,
        deliveryOptions?.arrivingTimeRange,
        planBudgetOrder,
        company?.ordersDiscount,
        company?.phone,
        couponDiscountAmount
    ]);

    const createEvent = useCallback(
        debounce(async () => {
            try {
                const {eventId, proposalId} = providerDetails?.isVoucherProvider
                    ? await createVoucherBookingEvent(valuesForCreateEvent, product, providerDetails, me?.userId)
                    : await createBookingEvent(
                          valuesForCreateEvent,
                          product,
                          providerDetails,
                          me?.features?.booking_offer_sockets,
                          me?.userId
                      );

                GoogleAnalytics.event("Professional Offer", "Proposal", me?.companyId);

                GoogleAnalytics.ecommerceEvent(
                    "purchase",
                    product,
                    serviceId,
                    {
                        transaction_id: eventId,
                        shipping: formValues?.deliveryPrice,
                        value: formValues?.offeredPrice,
                        checkout_option: paymentType,
                        coupon: coupon?.valid ? coupon.code : null
                    },
                    {
                        quantity: formValues.amount
                    }
                );

                log("Order Booked", {...valuesForCreateEvent});
                setCreatedEvent(eventId);
                setEventProposalId(proposalId);
                setPlanBudgetOrder(null);

                if (!me?.features?.booking_offer_sockets || providerDetails.isVoucherProvider) {
                    setBookingCompleteModalVisibility(true);
                }

                if (valuesForCreateEvent.paymentType === PaymentType.Budget) {
                    EventBus.trigger("me:updateBudget");
                }
            } catch (e) {
                EventBus.triggerError(
                    "server-error",
                    {
                        content: {
                            description: [
                                `Unfortunately we didn't manage to send your offer :(`,
                                `Error: ${e.message}`,
                                e.extraInfo
                            ]
                        }
                    },
                    e.message
                );
            }

            if (!me?.features?.booking_offer_sockets || providerDetails.isVoucherProvider) {
                setBookingLoading(false);
            }
        }, 50),
        [
            providerDetails,
            product?.durationMinutes,
            providerDetails,
            serviceId,
            paymentType,
            formValues?.offeredPrice,
            company?.address,
            me.companyId,
            me?.features?.booking_offer_sockets,
            formValues,
            coupon,
            formValues?.deliveryPrice,
            creditCard,
            upgradeOptionsPrice,
            log,
            valuesForCreateEvent,
            me?.isCompanyMaster,
            me?.phone
        ]
    );

    const officeAddress = useMemo(() => {
        if (isCompanySubAdmin(me) && me?.address) {
            return `${me?.address}${me?.floor ? ` קומה ${me?.floor}` : ""}`;
        } else if (me.isCompanyMaster && company?.address) {
            return `${company?.address}${company?.floor ? ` קומה ${company?.floor}` : ""}`;
        } else {
            return null;
        }
    }, [me, company]);

    const onBookingFinish = useCallback(async () => {
        const isMasterWithoutFinanceContact = me.isCompanyMaster && !company?.financeContact;
        const isSubAdminWithoutFinanceContact =
            me.isCompanySubAdmin && company?.separateSubAdminInvoices && !me.financeContact;

        const shouldShowFinanceContactModal =
            me?.features?.finance_contact_manage && (isMasterWithoutFinanceContact || isSubAdminWithoutFinanceContact);

        if (shouldShowFinanceContactModal) {
            setUpdateDetailsModals(FIRST_ORDER);
        } else if (!me?.isCompanyMaster && !me?.phone) {
            setShouldUpdateSubAdminDetails(true);
        } else {
            await createEvent();
        }
    }, [
        createEvent,
        me.isCompanyMaster,
        me.isCompanySubAdmin,
        me.financeContact,
        company?.financeContact,
        company?.separateSubAdminInvoices,
        me?.isCompanyMaster,
        me?.phone
    ]);

    const sendAlertToCMSs = useCallback(
        debounce(
            async (companyId, price, status, productName, productId, filledValues) =>
                sendAlertToCsmWithEmail(companyId, price, status, productName, productId, filledValues),
            1000
        ),
        []
    );

    const onFinishClick = useCallback(async () => {
        log("Execute Order Button Clicked");
        setBookingLoading(true);

        if (!bookingError) {
            await onBookingFinish();
            userFormValues.current = null;
        } else {
            if (formValues?.errors) {
                for (const errorKey in formValues.errors) {
                    if (formValues.errors[errorKey]?.level === "error") {
                        await sendAlertToCMSs(
                            me.companyId,
                            formValues?.offeredPrice,
                            formValues.errors[errorKey].message,
                            product.productName,
                            productId,
                            formValues
                        );
                        userFormValues.current = null;
                        break;
                    }
                }
            }

            message.error(bookingError ?? "יש למלא את כל שדות החובה לפני שליחת הזמנה", 3);

            if (BOOKING_ERROR_TO_PANEL[bookingError]) {
                setHighlightOrderPanel({
                    id: BOOKING_ERROR_TO_PANEL[bookingError],
                    color: BOOKING_TO_PANEL_COLORS.ERROR
                });
            }

            setBookingLoading(false);
        }
    }, [formValues?.error, onBookingFinish, bookingError, providerDetails, log]);

    const saveProductAsDraft = useCallback(async () => {
        const values = createBookingEventValues(valuesForCreateEvent, product, providerDetails);

        const draft = {
            ...formValues,
            ...values,
            productUrl: history.location.pathname,
            couponCode,
            upgradeOptions: {
                options: formValues?.upgradeOptions?.options || [],
                files: formValues?.upgradeOptions?.files || []
            }
        };

        try {
            const hide = message.loading("שומרים את ההזמנה...", 0);
            const res = await HttpClient.safePost("/api/savedOrder", draft);
            hide();
            message.success(
                <>
                    <>הזמנתך נשמרה בהצלחה</>
                    <Typography.Link
                        onClick={e => {
                            e.preventDefault();
                            history.push(`/company/profile/${company?.companyId}`);
                        }}>
                        לניהול הטיוטות שלי
                    </Typography.Link>
                </>,
                4
            );
            return res.savedOrederId;
        } catch (e) {
            hide(false);
            message.error("לא הצלחנו לשמור את ההזמנה", 3);
        }
    }, [formValues, couponCode, product, paymentType, providerId]);

    useEffect(() => {
        const validateCoupon = async code => {
            if (!company?.companyId) {
                return;
            }

            const order = {offeredPrice: formValues?.offeredPrice, companyId: company.companyId};
            const {
                error: requestError,
                discount,
                valid,
                errorMessage,
                discountType
            } = await HttpClient.safePost("/api/coupons/validate", {code, order});

            if (requestError) {
                EventBus.triggerError(
                    "server-error",
                    {content: {description: "Unfortunately we didn't manage to validate your coupon :("}},
                    requestError.message
                );
                return;
            }

            log("Coupon Validated", {code, discount, valid, errorMessage, discountType});
            setCoupon({valid, code, discount, errorMessage, discountType});
        };

        if (couponCode) {
            validateCoupon(couponCode);
        }
    }, [formValues?.offeredPrice, couponCode, company?.companyId]);

    useEffect(() => {
        if (highlightOrderPanel?.id) {
            setActiveKey(highlightOrderPanel?.id);
        }
    }, [highlightOrderPanel]);

    const tip = useMemo(
        () => getOrderTip(formValues?.offeredPrice, formValues.tipPercentage),
        [formValues?.offeredPrice, formValues.tipPercentage]
    );

    useEffect(() => {
        if (company?.ordersDiscount) {
            const totalOrderPrice =
                (formValues?.offeredPrice ?? 0) + (formValues?.deliveryPrice || 0) - (coupon?.discount || 0) + tip;
            const companyDiscount = Math.floor(totalOrderPrice * (company.ordersDiscount / 100));

            setFormValues(prevValues => ({...prevValues, companyDiscount}));
        }
    }, [company?.ordersDiscount, formValues?.offeredPrice, formValues?.deliveryPrice, coupon?.discount]);

    const onCancelCoupon = useCallback(() => {
        setCouponCode(null);
        setCoupon(null);
    }, []);

    useEffect(() => {
        if (!savedOrder) return;
        if (savedOrder?.productId !== productId) return;
        const {
            productName,
            providerId,
            productId: productIdFromSavedOrder,
            couponCode: couponCodeFromSavedOrder,
            paymentType: paymentTypeFromSavedOrder,
            date = null,
            time = null,
            address,
            companyRequests: requests,
            ...formValuesFromSavedOrder
        } = savedOrder;

        setFormValues({
            ...formValuesFromSavedOrder,
            ...(formValuesFromSavedOrder?.employeesDeliveryDetail?.length
                ? {singleEmployeeDetails: formValuesFromSavedOrder.employeesDeliveryDetail?.[0]}
                : {}),
            ...(date ? {date: moment(date)} : {}),
            ...(time ? {time: moment(time)} : {}),
            ...(requests ? {requests} : {})
        });
        if (couponCodeFromSavedOrder) {
            setCouponCode(couponCodeFromSavedOrder);
        }
        if (paymentTypeFromSavedOrder) {
            setPaymentType(paymentTypeFromSavedOrder);
        }
        setFormValues(formValues => ({
            ...formValues,
            upgradeOptions: {...(formValuesFromSavedOrder?.upgradeOptions || {})},
            chosenAddress: address || formValues.chosenAddress || null
        }));
    }, [savedOrder, productId]);

    const removeQueryParams = (searchQuery, keys) => {
        const searchParams = new URLSearchParams(searchQuery);
        for (const key of keys) {
            if (searchParams.has(key)) {
                searchParams.delete(key);
            }
        }
        return searchParams.toString();
    };

    const sendOrderAsQuote = useCallback(
        async receiverEmail => {
            const dateFormatted = moment(formValues.date).format("DD/MM/YYYY");
            const timeFormatted = moment(formValues.time).format("HH:mm");
            const dtstart = moment(`${dateFormatted} ${timeFormatted}`, "DD/MM/YYYY HH:mm")
                .tz(AsiaJerusalemTZ)
                .valueOf();
            const deliveryArrivingTimeRange =
                deliveryOptions?.arrivingTimeRange ?? DEFAULT_DELIVERY_ARRIVING_TIME_RANGE;
            const until = moment(dtstart).add(deliveryArrivingTimeRange, "hours").valueOf();
            const modularProducts = formValues.modularProducts?.filter(product => product?.amount > 0);

            const savedOrder = {
                ...formValues,
                serviceId,
                dtstart,
                until,
                paymentType,
                offeredPrice: formValues?.offeredPrice,
                address: formValues.chosenAddress || company.address,
                branches: company.branches?.[0] || null,
                companyPhone: company.phone,
                deliveryPrice: formValues?.deliveryPrice,
                coupon: coupon?.valid ? coupon : null,
                creditCard,
                modularProducts,
                upgradeOptionsPrice,
                deliveryArrivingTimeRange,
                companyDiscountPercentage: company.ordersDiscount
            };

            const bookingEventValues = createBookingEventValues(savedOrder, product, providerDetails);
            const hide = message.loading("שולח...", 0);

            const savedOrderId = await saveProductAsDraft();
            const {error} = await HttpClient.safePost("/api/sendRawSavedOrderToEmail", {
                savedOrder: {...bookingEventValues, chosenAddress: formValues.chosenAddress},
                receiverEmail,
                savedOrderId
            });
            hide();

            if (error) {
                message.error("אירעה שגיאה בעת שליחת המייל", 4);
                return;
            }

            await message.success("המייל נשלח בהצלחה", 4);
        },
        [
            formValues,
            deliveryOptions?.arrivingTimeRange,
            serviceId,
            paymentType,
            company?.address,
            company?.branches,
            company?.phone,
            company?.ordersDiscount,
            formValues?.deliveryPrice,
            coupon,
            creditCard,
            upgradeOptionsPrice,
            product,
            providerDetails,
            saveProductAsDraft
        ]
    );

    useEffect(() => {
        const clearSavedOrder = (newLocation, routerChangeType) => {
            if (location.pathname !== newLocation.pathname) {
                if (newLocation.pathname !== location.pathname || newSearch !== location.search) {
                    if (newLocation.search.length > 0) {
                        const newSearch = removeQueryParams(location.search, ["fromSavedOrder", "fromEventId"]);
                        history.replace({
                            pathname: newLocation.pathname,
                            search: newSearch
                        });
                    }
                }
            }
        };

        return history.listen(clearSavedOrder);
    }, [history, location]);

    const productServices = useMemo(() => {
        if (isProductBundle) {
            return uniq(flatten(productBundle.products.map(({services}) => services)));
        }

        return product?.services;
    }, [product, productBundle, isProductBundle]);

    const totalOrderPrice = useMemo(
        () =>
            getOrderPrice({
                offeredPrice: formValues?.offeredPrice,
                companyDiscountAmount: formValues.companyDiscount,
                couponDiscountAmount,
                deliveryPrice: formValues?.deliveryPrice ?? 0,
                tip: formValues?.tip
            }),
        [
            couponDiscountAmount,
            formValues?.deliveryPrice,
            formValues.companyDiscount,
            formValues?.offeredPrice,
            formValues?.tip
        ]
    );

    const totalLoadRequestBudgetAmount = useMemo(() => {
        const totalBudget = getTotalBudget(me?.budget, productServices);

        if (totalOrderPrice > totalBudget) {
            return Math.ceil(totalOrderPrice - totalBudget);
        }

        return 0;
    }, [
        couponDiscountAmount,
        formValues?.deliveryPrice,
        formValues.companyDiscount,
        formValues?.tip,
        me?.budget,
        formValues?.offeredPrice,
        productServices
    ]);

    const onSaveInvoiceDetails = useCallback(
        async financeContact => {
            await onSaveCompanyFinanceContact(financeContact);
            if (updateDetailsModals === FIRST_ORDER) {
                await createEvent();
            }

            if (me.isCompanySubAdmin) {
                await fetchMe();
            }

            await fetchCompany();
            setUpdateDetailsModals(null);
            setBookingLoading(false);
        },
        [createEvent, fetchCompany, fetchMe, me?.isCompanySubAdmin, onSaveCompanyFinanceContact, updateDetailsModals]
    );

    const onSaveSubAdmin = useCallback(
        async ({phone}) => {
            setConfirmButtonLoading(true);
            const res = await HttpClient.safePut(`/api/users/${me?.userId}`, {phone, userId: me?.userId});

            setConfirmButtonLoading(false);

            if (res?.error) {
                message.error(res.error);
                return;
            }
            fetchMe();

            await createEvent();

            setShouldUpdateSubAdminDetails(false);
        },
        [me?.userId, createEvent, fetchMe]
    );

    return (
        <MarketplaceBookingContext.Provider
            value={{
                deliveryAreaProperties: currentDeliveryArea,
                offeredPrice: formValues?.offeredPrice,
                highlightOrderPanel
            }}>
            <ProductBookingContext.Provider
                value={{
                    me,
                    company,
                    totalLoadRequestBudgetAmount,
                    onFinishClick,
                    officeAddress,
                    onFormValueChange,
                    onBudgetCategoriesPriorityChange,
                    onSeparateInvoiceChange,
                    onInvoicePONumberChange,
                    offeredPrice: formValues?.offeredPrice,
                    onInvoiceCommentChange,
                    currentDeliveryArea,
                    formValues,
                    setFormValues,
                    bookingError,
                    log,
                    activeKey,
                    setActiveKey,
                    calculateModularProductPrice,
                    setCreditCard,
                    setPaymentType,
                    setBookingLoading,
                    saveProductAsDraft,
                    updateDetailsModals,
                    setUpdateDetailsModals,
                    sendOrderAsQuote,
                    tip,
                    bookingLoading,
                    coupon,
                    couponDiscountAmount,
                    setCouponCode,
                    onCancelCoupon,
                    deliveryPrice: formValues?.deliveryPrice,
                    loadingDeliveryPrice,
                    upgradeOptionsPrice,
                    createEvent,
                    totalOrderPrice,
                    services,
                    onSyncUserBudget
                }}>
                <BookingCompleteModal
                    visible={bookingCompleteModalVisibility}
                    eventId={createdEvent}
                    proposalId={eventProposalId}
                    onSave={link => {
                        setBookingCompleteModalVisibility(false);
                        history.push(link);
                    }}
                    onClose={() => setBookingCompleteModalVisibility(false)}
                    company={company}
                    me={me}
                />

                {!!me?.features?.finance_contact_manage && (
                    <CompanyInvoiceManage
                        showAsModal={true}
                        visible={updateDetailsModals}
                        onClose={() => {
                            setUpdateDetailsModals(null);
                            setBookingLoading(false);
                        }}
                        onSave={onSaveInvoiceDetails}
                        me={me}
                        company={company}
                    />
                )}

                {!!(
                    isSocketError &&
                    bookingLoading &&
                    me?.features?.booking_offer_sockets &&
                    !providerDetails.isVoucherProvider
                ) && (
                    <TickerNotification
                        contentString="קיבלנו את בקשתך, וההזמנה שלך בדרך להיכנס למערכת! אנחנו כאן, ונעדכן אותך במייל ובסמס ברגע שזה יקרה"
                        onClose={NOOP}
                    />
                )}

                {!me?.isCompanyMaster && (
                    <ConfigProvider direction="rtl">
                        <EditSubAdminModal
                            header="חסר לנו רק דבר קטן 📞"
                            subTitle="נראה שאין לנו את הטלפון שלך במערכת. כדי להשלים את הפעולה יש להזין אותו"
                            visible={shouldUpdateSubAdminDetails}
                            onSave={onSaveSubAdmin}
                            mainServices={services}
                            onClose={() => setShouldUpdateSubAdminDetails(false)}
                            subAdmin={me}
                            isConfirmButtonLoading={isConfirmButtonLoading}
                            hideFields={editSubAdminPhoneHideFormFields}
                        />
                    </ConfigProvider>
                )}

                {children}
            </ProductBookingContext.Provider>
        </MarketplaceBookingContext.Provider>
    );
};
